/**
 * Dismissal of the inline splash in index.html.
 *
 * The splash used to be hidden at module-eval time in index.tsx — the instant the bundle ran,
 * which is *before* React renders and before the app's own startup requests (feature flags, the
 * site-password check) have returned. `SitePasswordGate` renders `null` until those resolve, so
 * the splash disappeared and left a blank white page for the length of a network round trip.
 * That gap is the "noticeable lag" this exists to cover.
 *
 * So dismissal belongs with whoever first has something to show, not with the module loader.
 */

const SPLASH_ID = 'initial-loading-screen'

/**
 * A splash that is never dismissed would trap the user behind it, so this is the backstop: if the
 * app has not called {@link dismissSplash} by the time it fires, take the splash down anyway and
 * let whatever the app managed to render through. Failing visible beats failing blank.
 */
const FAILSAFE_MS = 10_000

let dismissed = false

/** Fades the splash out and removes it. Safe to call more than once, and before the DOM is ready. */
export function dismissSplash(): void {
  if (dismissed) return
  dismissed = true

  const splash = document.getElementById(SPLASH_ID)
  if (!splash) return

  splash.classList.add('is-dismissing')

  // Remove rather than just hide: it is a fixed, full-viewport element, and leaving it in the
  // tree would keep it over the app if anything later cleared the class.
  const remove = () => splash.remove()
  splash.addEventListener('transitionend', remove, { once: true })
  // transitionend does not fire when the element is already invisible (a background tab, or
  // reduced-motion turning the transition off), so it cannot be the only path.
  window.setTimeout(remove, 400)
}

if (typeof window !== 'undefined') {
  window.setTimeout(dismissSplash, FAILSAFE_MS)
}
